import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Award, CalendarCheck, CalendarDays, Check, ChevronRight, CircleCheck, CircleDashed, Clock, Coffee, Flag, Repeat2, Timer,
  TriangleAlert,
} from 'lucide-react';
import { apiErrorMessage, trailService } from '../services/api';
import { Breadcrumbs, Button, EmptyState, GlobalShell, Loader, PageHeader, ScoreCard, SectionTitle } from '../components/ui';
import { AvailabilityModal } from '../components/trails/TrailBits';
import { PARTS, SEALS, WEEKDAYS, fmtHours, fmtHoursRound, trailCrumbs } from '../utils/trails';
import './TrailPlan.css';

const SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const WEEK = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];
const WEEK_LONG = ['segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado', 'domingo'];
const PART_BY_LABEL = Object.fromEntries(Object.values(PARTS).map(p => [p.label, p]));

const parse = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
// 0 = segunda … 6 = domingo
const weekIndex = (date) => (date.getDay() + 6) % 7;
const fmtDay = (date) => `${date.getDate()} ${SHORT[date.getMonth()]}`;

/** 1d: calendário só com os dias de estudo em destaque, o placar e os blocos de hoje. */
function TrailPlan() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [replanning, setReplanning] = useState(false);

  const load = useCallback(() => {
    setError(null);
    trailService.plan(slug).then(setPlan).catch(err => setError({ status: err?.response?.status, message: apiErrorMessage(err) }));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const weeks = useMemo(() => (plan ? buildWeeks(plan) : []), [plan]);
  // Coluna de estudo: dia escolhido no plano, ou um dia da semana que ainda tem estudo marcado
  const studyCols = useMemo(() => {
    if (!plan) return [];
    const cols = new Set((plan.weekdays || []).map(n => n - 1));
    plan.days.forEach(d => cols.add(weekIndex(parse(d.date))));
    return WEEK.map((_, i) => cols.has(i));
  }, [plan]);

  if (error) {
    return (
      <GlobalShell>
        <EmptyState
          file="plano.xml"
          title={error.status === 409 ? 'Você ainda não começou esta trilha' : 'Não foi possível abrir o plano'}
          description={error.status === 409 ? 'Comece a trilha para montar o plano com as suas horas.' : error.message}
          action={<Button onClick={() => navigate(`/trilhas/${slug}`)}>Ver a trilha</Button>}
        />
      </GlobalShell>
    );
  }
  if (!plan) return <GlobalShell><Loader label="Montando o plano..." /></GlobalShell>;

  const inRange = weeks.flat().filter(c => c.inRange).map(c => c.date);
  const legend = [...new Map(plan.days.filter(d => d.stage).map(d => [d.stage.split(' · ')[0], d.color])).entries()];
  const cols = studyCols.map(on => (on ? 'minmax(0, 1fr)' : '40px')).join(' ');
  const weekdays = new Set(plan.weekdays || []);
  const today = plan.days.find(d => d.today);
  const next = plan.days.find(d => !d.today && parse(d.date) > new Date());
  const openStage = (n) => n != null && navigate(`/trilhas/${slug}/etapas/${n}`);

  return (
    <GlobalShell trail={plan.trail}>
      <div className="ss-page ss-page--wide ss-page--crumbs trp">
        <Breadcrumbs items={[
          ...trailCrumbs(navigate, plan.trail),
          { label: 'Plano' },
        ]} />
        <div className="trp-head">
          <PageHeader
            icon={CalendarDays}
            eyebrow={plan.trail.shortTitle}
            title="Plano de estudo"
            subtitle={plan.subtitle}
            actions={(
              <div className="trp-actions">
                <div className="trp-actions__row">
                  <Button variant="outline" icon={Repeat2} onClick={() => setReplanning(true)}>Replanejar</Button>
                  <a className="ss-btn ss-btn--primary ss-btn--md trp-ics" href={trailService.icsUrl(slug)} download>
                    <CalendarCheck size={18} aria-hidden="true" /><span>Exportar para o calendário</span>
                  </a>
                </div>
                <p className="trp-days" title={plan.note || undefined}>
                  <span>Estuda em</span>
                  <span className="trp-days__list" aria-label={WEEKDAYS.filter(w => weekdays.has(w.n)).map(w => w.short).join(', ')}>
                    {WEEKDAYS.map((w, i) => (
                      <span key={w.n} className={weekdays.has(w.n) ? 'is-on' : ''} title={WEEK_LONG[i]} aria-hidden="true">
                        {w.short[0]}
                      </span>
                    ))}
                  </span>
                  {plan.hoursPerDay != null && <span>· até {plan.hoursPerDay}h/dia</span>}
                </p>
              </div>
            )}
          />
        </div>

        <ScoreCard
          inline
          items={[
            { icon: Clock, value: fmtHoursRound(plan.plannedMinutes), label: 'Planejadas', hint: `em ${plan.days.length} dias` },
            {
              icon: CircleCheck, value: fmtHoursRound(plan.doneMinutes), label: 'Feitas', tone: 'lime',
              hint: plan.doneDays || (plan.doneMinutes ? 'nos recursos em andamento' : 'ainda nenhuma'),
            },
            { icon: Timer, value: plan.todayMinutes ? fmtHours(plan.todayMinutes) : '0h', label: 'Hoje', hint: plan.todayText },
            { icon: Flag, value: plan.daysToExam ?? '—', label: 'Dias até a prova', hint: plan.examLabel || 'sem data marcada' },
          ]}
        />

        {plan.overflowMinutes > 0 && (
          <p className="trp-warn" role="alert">
            <TriangleAlert size={17} aria-hidden="true" />
            Faltam {fmtHoursRound(plan.overflowMinutes)} antes da prova. Aumente as horas por dia em Replanejar ou mude a data.
          </p>
        )}

        <div className="trp-grid">
          <section className="trp-main">
            <SectionTitle
              icon={CalendarDays}
              aside={(
                <span className="trp-legend">
                  {legend.map(([label, color]) => <span key={label}><i style={{ background: color }} />{label}</span>)}
                  <span className="trp-legend__sep" aria-hidden="true" />
                  <span className="trp-legend__count is-done" title="Dias concluídos">
                    <Check size={12} strokeWidth={2.6} aria-hidden="true" />{plan.statusCounts.done}
                  </span>
                  <span className="trp-legend__count is-late" title="Dias atrasados">
                    <Clock size={12} strokeWidth={2.6} aria-hidden="true" />{plan.statusCounts.late}
                  </span>
                  <span className="trp-legend__count" title="Dias pendentes">
                    <CircleDashed size={12} strokeWidth={2.6} aria-hidden="true" />{plan.statusCounts.pending}
                  </span>
                </span>
              )}
            >
              {monthsTitle(inRange)}
            </SectionTitle>
            <div className="trp-cal" role="grid" aria-label="Calendário do plano" style={{ gridTemplateColumns: cols }}>
              {WEEK.map((w, i) => (
                <span key={w} className={`trp-cal__wd ${studyCols[i] ? '' : 'is-off'}`} role="columnheader" title={WEEK_LONG[i]}>
                  {studyCols[i] ? w : w[0]}
                </span>
              ))}
              {weeks.flat().map(cell => (
                <Cell key={cell.key} cell={cell} plan={plan} study={studyCols[weekIndex(cell.date)]} />
              ))}
            </div>
          </section>

          <aside className="trp-side">
            {plan.todayBlocks.length > 0 ? (
              <TodayCard plan={plan} today={today} onOpen={openStage} />
            ) : (
              <FreeDay plan={plan} next={next} />
            )}
          </aside>
        </div>
      </div>

      <AvailabilityModal
        open={replanning}
        mode="replan"
        trail={plan.trail}
        initial={{ hours: plan.hoursPerDay, weekdays: plan.weekdays, examDate: plan.examDate || '', noDate: !plan.examDate }}
        onClose={() => setReplanning(false)}
        onDone={() => { setReplanning(false); load(); }}
      />
    </GlobalShell>
  );
}

/** Hoje com estudo: topo navy, blocos que abrem a etapa e a barra do que já foi feito. */
function TodayCard({ plan, today, onOpen }) {
  const doneMinutes = today ? Math.round(today.minutes * (today.donePct || 0) / 100) : 0;
  return (
    <section className="trp-today" data-tour="plano-hoje">
      <div className="trp-today__top">
        <div>
          <span className="trp-today__eyebrow">Hoje · {plan.todayLabel}</span>
          <span className="trp-today__sub">{plan.todayText}</span>
        </div>
        {plan.todayMinutes > 0 && <strong>{fmtHours(plan.todayMinutes)}</strong>}
      </div>
      <ul className="trp-blocks">
        {plan.todayBlocks.map(b => {
          const Part = b.done ? Check : b.late ? Clock : (PART_BY_LABEL[b.part]?.icon || CircleDashed);
          const where = b.stageN === 0 ? 'Antes de começar' : b.stageN === 99 ? 'Reta final' : `Etapa ${b.stageN}`;
          return (
            <li key={`${b.resourceId}-${b.from}`}>
              <button
                type="button"
                className={`trp-block ${b.late ? 'is-late' : ''} ${b.done ? 'is-done' : ''}`}
                title={b.stageN != null ? `Abrir ${where}` : undefined}
                onClick={() => onOpen(b.stageN)}
              >
                <span className="trp-block__time"><span>{b.from}</span><span>{b.to}</span></span>
                <span className="trp-block__body" style={{ '--stage': b.color }}>
                  <span className="trp-block__part">
                    <Part size={11} strokeWidth={2.6} aria-hidden="true" />
                    {b.late ? 'Atrasado' : b.part}{b.seal && ` · ${SEALS[b.seal]?.label || b.seal}`}
                  </span>
                  <span className="trp-block__title">{b.title}</span>
                </span>
                <ChevronRight size={14} className="trp-block__chev" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {today && today.minutes > 0 && (
        <div className="trp-today__foot">
          <span className="trp-today__bar"><span style={{ width: `${Math.min(100, today.donePct || 0)}%` }} /></span>
          <span>{fmtHours(doneMinutes)} de {fmtHours(today.minutes)}</span>
        </div>
      )}
    </section>
  );
}

/** Hoje sem estudo: dia livre e o próximo dia do plano. */
function FreeDay({ plan, next }) {
  const date = next ? parse(next.date) : null;
  return (
    <section className="trp-today" data-tour="plano-hoje">
      <div className="trp-today__top trp-today__top--free">
        <span className="trp-today__coffee"><Coffee size={17} aria-hidden="true" /></span>
        <div>
          <span className="trp-today__eyebrow">Hoje · {plan.todayLabel}</span>
          <span className="trp-today__free">Dia livre</span>
        </div>
      </div>
      <div className="trp-next">
        {next ? (
          <>
            <span className="trp-next__label">Próximo estudo</span>
            <div className="trp-next__row">
              <span>{WEEK_LONG[weekIndex(date)]}, {fmtDay(date)}</span>
              <strong>{fmtHours(next.minutes)}</strong>
            </div>
            <span className="trp-next__stage">Dia {next.n}{next.stage ? ` · ${next.stage}` : ''}</span>
          </>
        ) : (
          <span className="trp-next__stage">Não há mais dias de estudo no plano.</span>
        )}
      </div>
    </section>
  );
}

/** "OUT 2026", "OUT – DEZ 2026" ou "DEZ 2026 – JAN 2027". */
function monthsTitle(dates) {
  if (!dates.length) return 'Calendário';
  const a = dates[0];
  const b = dates[dates.length - 1];
  const m = (d) => SHORT[d.getMonth()];
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return `${m(a)} ${a.getFullYear()}`;
  if (a.getFullYear() === b.getFullYear()) return `${m(a)} – ${m(b)} ${b.getFullYear()}`;
  return `${m(a)} ${a.getFullYear()} – ${m(b)} ${b.getFullYear()}`;
}

/** Semanas (seg a dom) do primeiro dia do plano até o "Depois". */
function buildWeeks(plan) {
  const byDate = new Map(plan.days.map(d => [d.date, d]));
  const first = plan.days[0] ? parse(plan.days[0].date) : new Date();
  const lastPlan = plan.days.length ? parse(plan.days[plan.days.length - 1].date) : first;
  const exam = plan.examDate ? parse(plan.examDate) : null;
  const end = exam && exam > lastPlan ? exam : lastPlan;
  const after = addDays(end, 1);
  const start = addDays(first, -weekIndex(first));
  const stop = addDays(after, 6 - weekIndex(after));
  const todayKey = iso(new Date());
  const weeks = [];
  for (let d = start; d <= stop; d = addDays(d, 7)) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(d, i);
      const key = iso(date);
      week.push({
        key,
        date,
        day: byDate.get(key),
        exam: exam && key === iso(exam),
        after: key === iso(after),
        today: key === todayKey,
        inRange: date >= first && date <= after,
      });
    }
    weeks.push(week);
  }
  return weeks;
}

function Cell({ cell, plan, study }) {
  const { date, day } = cell;
  const first = cell.key === plan.days[0]?.date;
  const label = date.getDate() === 1 || first ? fmtDay(date) : String(date.getDate());
  if (cell.exam) {
    return (
      <div className="trp-cell trp-cell--exam" role="gridcell" title={`Prova${plan.examTime ? ` às ${plan.examTime}` : ''}`}>
        <span className="trp-cell__date">{study ? label : date.getDate()}</span>
        <Flag size={study ? 18 : 14} aria-hidden="true" />
        {study && <strong>Prova</strong>}
        {study && plan.examTime && <span className="trp-cell__exam-time">{plan.examTime}</span>}
      </div>
    );
  }
  if (cell.after && !day) {
    return (
      <div className="trp-cell trp-cell--after" role="gridcell" title="Depois da trilha">
        <span className="trp-cell__date">{study ? label : date.getDate()}</span>
        {study ? (
          <span className="trp-cell__after"><Award size={13} aria-hidden="true" />Depois</span>
        ) : <Award size={13} className="trp-cell__after" aria-label="Depois" />}
      </div>
    );
  }
  if (!day) {
    // Coluna livre: só o número; dia de estudo fora do plano: apagado
    const cls = study ? 'trp-cell--idle' : 'trp-cell--off';
    return (
      <div className={`trp-cell ${cls} ${cell.inRange ? '' : 'is-out'} ${cell.today ? 'is-today-free' : ''}`} role="gridcell">
        <span className="trp-cell__date">{study ? label : date.getDate()}</span>
      </div>
    );
  }
  const cls = day.today ? 'is-today' : day.status === 'late' ? 'is-late' : day.status === 'done' ? 'is-done' : '';
  return (
    <div
      className={`trp-cell trp-cell--study ${cls}`}
      role="gridcell"
      style={{ '--stage': day.color }}
      title={`${fmtDay(date)} · Dia ${day.n} · ${fmtHours(day.minutes)}${day.stage ? ` · ${day.stage}` : ''}`}
    >
      <span className="trp-cell__top">
        <span className="trp-cell__date">{label} · D{day.n}</span>
        {day.today && <span className="trp-cell__today">Hoje</span>}
        {!day.today && day.status === 'late' && <Clock size={13} className="trp-cell__late" aria-label="atrasado" />}
        {!day.today && day.status === 'done' && <Check size={13} strokeWidth={2.6} className="trp-cell__done" aria-label="concluído" />}
      </span>
      <strong className="trp-cell__hours">{fmtHours(day.minutes)}</strong>
      <span className="trp-cell__stage">{day.stageTitle || day.stage}</span>
    </div>
  );
}

export default TrailPlan;
